import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import UserContext from "context/UserContext";
import performRequest from "network/perform-request";
import {
  createMemberCategoryAssignmentRoute,
  getAssignableOfficialCategoriesRoute,
  getMemberCategoryAssignmentsRoute,
} from "network/api-routes";

export default function OfficialCategoryAssignmentPanel({
  organization,
  memberId,
  currentAssignments = [],
  onChanged,
  compact = false,
}) {
  const { user } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const movementLabels = {
    initial: intl.formatMessage({ id: "Asignación inicial" }),
    promotion: intl.formatMessage({ id: "Ascenso" }),
    relegation: intl.formatMessage({ id: "Descenso" }),
    correction: intl.formatMessage({ id: "Corrección" }),
  };
  const [categories, setCategories] = useState([]);
  const [history, setHistory] = useState([]);
  const [current, setCurrent] = useState(currentAssignments);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ category_id: "", movement_type: "initial", reason: "" });

  const load = useCallback(() => {
    if (!organization?.id || !memberId) return;
    setLoading(true);
    Promise.all([
      performRequest("GET", getAssignableOfficialCategoriesRoute(organization.id), {}, true, user),
      performRequest("GET", getMemberCategoryAssignmentsRoute(organization.id, memberId), {}, true, user),
    ])
      .then(([categoriesResponse, historyResponse]) => {
        setCategories(categoriesResponse.data.data || []);
        const nextHistory = historyResponse.data.data || [];
        setHistory(nextHistory);
        setCurrent(nextHistory.filter((assignment) => !assignment.valid_until));
      })
      .catch((error) =>
        enqueueSnackbar(
          error?.response?.data?.message ||
            intl.formatMessage({ id: "No se pudieron cargar las categorías oficiales" }),
          { variant: "error" },
        ),
      )
      .finally(() => setLoading(false));
  }, [enqueueSnackbar, intl, memberId, organization?.id, user]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setCurrent(currentAssignments);
  }, [currentAssignments]);

  const submit = () => {
    if (!form.category_id || (form.movement_type !== "initial" && !form.reason.trim())) return;
    setSaving(true);
    performRequest("POST", createMemberCategoryAssignmentRoute(organization.id, memberId), form, true, user)
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "Categoría oficial actualizada" }), { variant: "success" });
        setOpen(false);
        setForm({ category_id: "", movement_type: "initial", reason: "" });
        load();
        onChanged?.();
      })
      .catch((error) =>
        enqueueSnackbar(
          error?.response?.data?.message || intl.formatMessage({ id: "No se pudo actualizar la categoría" }),
          { variant: "error" },
        ),
      )
      .finally(() => setSaving(false));
  };

  return (
    <Box mt={compact ? 0 : 4}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">{intl.formatMessage({ id: "Categoría oficial" })}</Typography>
        <Button size="small" variant="outlined" disabled={loading} onClick={() => setOpen(true)}>
          {intl.formatMessage({ id: current.length ? "Cambiar categoría" : "Asignar categoría" })}
        </Button>
      </Stack>
      {current.length ? (
        current.map((assignment) => (
          <Alert key={assignment.id} severity="info" sx={{ mb: 1 }}>
            {assignment.category.name} · {assignment.category.ladder.name} ·{" "}
            {intl.formatMessage(
              { id: "Vigente desde {date}" },
              { date: new Date(assignment.valid_from).toLocaleDateString() },
            )}
          </Alert>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          {intl.formatMessage({ id: "No hay una categoría oficial vigente." })}
        </Typography>
      )}
      {!loading && categories.length === 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {intl.formatMessage({
            id: "Primero debe existir un ranking no archivado con temporada vigente para habilitar categorías.",
          })}
        </Alert>
      )}
      {history.length > 0 && (
        <Stack spacing={0.5} mt={2}>
          <Typography variant="subtitle2">{intl.formatMessage({ id: "Historial" })}</Typography>
          {history.map((assignment) => (
            <Typography key={assignment.id} variant="body2">
              {assignment.category.name} · {movementLabels[assignment.movement_type]} ·{" "}
              {new Date(assignment.valid_from).toLocaleDateString()}
              {assignment.valid_until
                ? ` · ${intl.formatMessage({ id: "Hasta {date}" }, { date: new Date(assignment.valid_until).toLocaleDateString() })}`
                : ""}
              {assignment.reason ? ` · ${assignment.reason}` : ""}
            </Typography>
          ))}
        </Stack>
      )}
      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {intl.formatMessage({ id: current.length ? "Cambiar categoría oficial" : "Asignar categoría oficial" })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <TextField
              select
              required
              label={intl.formatMessage({ id: "Categoría" })}
              value={form.category_id}
              onChange={(event) => setForm({ ...form, category_id: event.target.value })}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {intl.formatMessage({ id: category.modality === "male" ? "Masculino" : "Femenino" })} ·{" "}
                  {category.ladder.name} · {category.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={intl.formatMessage({ id: "Movimiento" })}
              value={form.movement_type}
              onChange={(event) => setForm({ ...form, movement_type: event.target.value })}
            >
              {Object.entries(movementLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={intl.formatMessage({ id: "Motivo" })}
              value={form.reason}
              required={form.movement_type !== "initial"}
              onChange={(event) => setForm({ ...form, reason: event.target.value })}
              helperText={intl.formatMessage({
                id:
                  form.movement_type === "initial"
                    ? "Opcional para la asignación inicial."
                    : "Obligatorio para este movimiento.",
              })}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setOpen(false)}>
            {intl.formatMessage({ id: "Cancelar" })}
          </Button>
          <Button
            disabled={saving || !form.category_id || (form.movement_type !== "initial" && !form.reason.trim())}
            onClick={submit}
            variant="contained"
          >
            {intl.formatMessage({ id: saving ? "Guardando…" : "Confirmar" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
